import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useSession } from '../context/SessionContext';

export default function usePublicationController() {
  const { session } = useSession();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch publications from the database
  const fetchPublications = async () => {
    try {
      setLoading(true);
      const { data: publications, error: publicationsError } = await supabase
        .from('publication')
        .select('*')
        .eq('user_id', session.user.id);

      if (publicationsError) throw publicationsError;

      setPublications(publications);
    } catch (error) {
      console.error('Error fetching publications:', error);
      setError('Failed to fetch publications');
    } finally {
      setLoading(false);
    }
  };

  // Add a new publication
  const addPublication = async (publicationData) => {
    try {
      setLoading(true);
      const { error: publicationError } = await supabase
        .from('publication')
        .insert([{ 
          ...publicationData, 
          user_id: session.user.id,
        }])
        .select()
        .single();

      if (publicationError) throw publicationError;

      // Refresh publications list
      await fetchPublications();
    } catch (error) {
      console.error('Error adding publication:', error);
      setError('Failed to add publication');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing publication
  const updatePublication = async (id, publicationData) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('publication')
        .update({
          ...publicationData,
          date: publicationData.date // Biarkan format asli (YYYY-MM-DD)
        })
        .eq('id', id)
        .select();

      if (error) throw error;
      
      // Perbarui state dengan data terbaru dari server
      setPublications(prev => prev.map(pub => 
        pub.id === id ? { ...pub, ...data[0] } : pub
      ));
      
      return data[0];
    } catch (error) {
      console.error('Error updating publication:', error);
      setError('Failed to update publication');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a publication
  const deletePublication = async (id) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('publication')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh publications list
      await fetchPublications();
    } catch (error) {
      console.error('Error deleting publication:', error);
      setError('Failed to delete publication');
    } finally {
      setLoading(false);
    }
  };

  // Fetch publications on component mount
  useEffect(() => {
    if (session) {
      fetchPublications();
    }
  }, [session]);

  return {
    publications,
    loading,
    error,
    addPublication,
    updatePublication,
    deletePublication,
  };
}